import { Stack, Text, useToast } from '@chakra-ui/react';
import { NotificationCategory } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { clarificationReasonOptions, getPrettyClarificationMessage } from '@pelicargo/utils';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';

import { trpc } from '../../config/trpc';
import { useCustomerClarificationRequestForm } from '../../hooks/emails/useCustomerClarificationRequestForm';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useEmail } from '../../hooks/useEmail';
import { hasCustomerEmail } from '../../utils/email';
import { EmailDrawer } from '../Emails/EmailDrawer';
import { EmailDrawerLeftPanel } from '../Emails/EmailDrawerLeftPanel';
import { EmailDrawerRightPanel } from '../Emails/EmailDrawerRightPanel';
import { EmailQuoteForm } from '../Emails/EmailQuoteForm';
import { useRequestDrawersContext } from '../RequestDrawers';
import { SimpleCustomerRecipientForm } from '../SimpleCustomerRecipientForm';
import { SimpleForm } from '../SimpleForm';
import { SimpleSelect } from '../SimpleSelect';

export const RequestClarificationsDrawer = () => {
  const toast = useToast();
  const { request } = useCurrentRequest();
  const { data: contacts } = trpc.getUsersContacts.useQuery(
    { ids: [request?.default_sender_contact?.user?.id] },
    { enabled: !!request?.default_sender_contact?.user?.id },
  );
  const { data: defaultContacts } = trpc.getDefaultContactsByRequest.useQuery(
    { id: request?.id, updates_for: NotificationCategory.Quote },
    { enabled: !!request?.id },
  );
  const { methods } = useCustomerClarificationRequestForm(request);
  const recipients = methods.watch('recipients');
  const defaultSelectedContacts = uniqBy([...(contacts || []), ...(defaultContacts || [])].filter(Boolean), 'id');

  const { sendRequestClarificationNotification, isLoading } = useEmail();

  const { getIsModalOpen, closeModal } = useRequestDrawersContext();
  const isOpen = getIsModalOpen('RequestClarifications');
  const onClose = () => closeModal('RequestClarifications');

  const handleSendEmail = async (overrides: any) => {
    try {
      const requestId = request?.id;
      if (!requestId) return;

      const { senderContactId } = overrides;

      const toContact = recipients?.[0];

      const ccContacts = recipients?.slice(1);

      const reason = methods.getValues('clarificationReason');

      await sendRequestClarificationNotification({
        requestId,
        senderContactId,
        toContact,
        ccContacts,
        overrides: {
          ...overrides,
          clarificationReason: reason,
        },
      });

      toast({
        title: 'Success',
        description: overrides?.clipboard ? 'Email copied to clipboard' : 'Email sent successfully',
        status: 'success',
      });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  const canSend = !!recipients?.length;

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const reason = methods.watch('clarificationReason');
  const sampleEmailCopyLines = useMemo(() => {
    return getPrettyClarificationMessage(reason, '(num of pieces)', '(gross weight)', '(kg/lbs)')?.split('\n');
  }, [reason]);

  const customMessage = methods.watch('customMessage');
  const defaultRequest = {
    ...request,
    customMessage,
  };

  const showWarning = useMemo(() => {
    if (!recipients?.length) return false;

    return hasCustomerEmail(recipients);
  }, [recipients]);

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <EmailDrawer isOpen={isOpen} onClose={handleClose}>
        <EmailDrawerLeftPanel
          title="Specify contact(s)"
          description="Specify the contacts you want to send the email to"
          w="50%"
        >
          <SimpleCustomerRecipientForm
            name="recipients"
            defaultAccount={request?.account}
            defaultSelectedContacts={defaultSelectedContacts}
            searchPattern="FF"
          />
        </EmailDrawerLeftPanel>
        <EmailDrawerRightPanel title="Request Clarification" w="50%">
          <SimpleSelect name="clarificationReason" label="Reason for Contact" options={clarificationReasonOptions} />
          <Show if={!!sampleEmailCopyLines?.length}>
            <Stack borderWidth={1} borderRadius="md" p={4}>
              {sampleEmailCopyLines?.map((line) => (
                <Text key={line} fontSize="sm">
                  {line}
                </Text>
              ))}
              <Show if={!!customMessage}>
                <Text fontSize="sm">{customMessage}</Text>
              </Show>
            </Stack>
          </Show>
          <EmailQuoteForm
            request={defaultRequest}
            onSubmit={handleSendEmail}
            onClose={onClose}
            isLoading={isLoading}
            canSend={canSend}
            hasCustomerEmail={showWarning}
          />
        </EmailDrawerRightPanel>
      </EmailDrawer>
    </SimpleForm>
  );
};
